import { ElementType, FC, type KeyboardEvent as ReactKeyboardEvent, ReactNode, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import { Box, SxProps, Theme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema, type Options } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { FileTextIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import { asVisionAnalysis, VisionFigure } from './VisionFigure';

/**
 * Base typography for rendered markdown. Everything inherits font and colour
 * from the container so a caller only has to style the wrapper, not each tag.
 */
const BLOCK_SX: SxProps<Theme> = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  '& > :first-of-type': { marginTop: 0 },
  '& > :last-child': { marginBottom: 0 },
  '& p': { margin: '0 0 0.75em' },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '1.2em 0 0.5em',
    fontWeight: 700,
    lineHeight: 1.35,
    fontSize: '1.05em',
  },
  '& ul, & ol': { margin: '0 0 0.75em', paddingInlineStart: '1.4em' },
  '& li': { marginBottom: '0.2em' },
  '& li::marker': { color: 'inherit' },
  '& a': { color: 'primary.main' },
  '& strong': { fontWeight: 700 },
  '& blockquote': {
    margin: '0 0 0.75em',
    paddingInlineStart: '0.9em',
    borderInlineStart: '3px solid',
    borderColor: 'grey.300',
    color: 'text.secondary',
  },
  '& hr': { border: 0, borderTop: '1px solid', borderColor: 'grey.300', margin: '1em 0' },
  // The scroll lives on the wrapper (see `TableScroll`), never on the table
  // itself — `display: block` on a <table> collapses its column layout.
  '& .md-table-scroll': { overflowX: 'auto', margin: '0.75em 0', maxWidth: '100%' },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    minWidth: 'max-content',
    fontSize: '0.95em',
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    padding: '0.4rem 0.6rem',
    textAlign: 'start',
    verticalAlign: 'top',
  },
  '& th': { backgroundColor: 'grey.100', fontWeight: 600, whiteSpace: 'nowrap' },
  '& tbody tr:nth-of-type(even)': { backgroundColor: 'action.hover' },
  '& pre': {
    backgroundColor: 'grey.100',
    padding: '0.75rem',
    borderRadius: '4px',
    overflowX: 'auto',
    margin: '0.5em 0',
  },
  '& code': { fontFamily: 'monospace', fontSize: '0.85em' },
  '& img': { maxWidth: '100%' },
};

/**
 * What raw HTML in AI output is allowed to be.
 *
 * Documents come back from the AI as markdown, but real ones arrive carrying
 * HTML — a table the model chose to write as `<table>`, a `<strong>`, a `<br>`.
 * Printed as source (which is what happens when raw HTML is simply disabled)
 * they are unreadable, and a compliance document that renders its own markup as
 * literal text is not usable output.
 *
 * So the markup is rendered, and every dangerous thing is removed first. The
 * base is `hast-util-sanitize`'s GitHub-derived allowlist: `script` is stripped,
 * `style`/`iframe`/`object`/`form` are not in `tagNames`, no `on*` handler is in
 * `attributes`, and `protocols` confines `href`/`src` to http(s) and friends, so
 * `javascript:` cannot survive. Nothing here widens any of that.
 *
 * The one addition is `language-*` on `code`. The default allows the attribute
 * but permits no values, which would strip the class silently — and `CodeFence`
 * keys off `language-json` to render an embedded image's vision analysis as a
 * figure instead of as raw JSON. Losing it would not error; it would just
 * quietly go back to showing readers a wall of escaped braces.
 */
const SANITIZE_SCHEMA: Options = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'sup'],
  attributes: {
    ...defaultSchema.attributes,
    code: [['className', /^language-/]],
    // `data-citation` carries the reference number an inline `[n]` marker points
    // at (see the chat `linkifyCitations` util). Values are digits only, so this
    // widens the allowlist by an inert numeric attribute and nothing else.
    //
    // Both spellings are listed deliberately. This allowlist is matched against
    // the *hast property* name, and `rehype-raw` parses `data-citation="1"` into
    // the property `dataCitation` — so the hyphenated key alone silently stripped
    // the attribute, leaving the badge with no source to name and rendering a bare
    // number. Keeping both survives that mapping either way.
    sup: [
      ['dataCitation', /^\d{1,3}$/],
      ['data-citation', /^\d{1,3}$/],
    ],
  },
};

const Passthrough: FC<{ children?: ReactNode }> = observer(({ children }) => <>{children}</>);

/** A wide table has to scroll inside its own box, not push the page sideways. */
const TableScroll: FC<{ children?: ReactNode }> = observer(({ children }) => (
  <div className="md-table-scroll">
    <table>{children}</table>
  </div>
));

/**
 * A ```json fence carrying an embedded image's vision analysis is rendered as a
 * figure rather than as source. The intake pipeline substitutes that payload for
 * the image itself, so it is document *content* — it was reaching readers as raw
 * braces and escaped newlines in the middle of otherwise readable text. Any
 * other code fence is left exactly as it was.
 */
const CodeFence: FC<{ className?: string; children?: ReactNode }> = observer(
  ({ className, children }) => {
    if ((className ?? '').includes('language-json')) {
      try {
        const parsed = asVisionAnalysis(JSON.parse(String(children)));
        if (parsed) return <VisionFigure data={parsed} />;
      } catch {
        // Not parseable — fall through and render it as the code block it is.
      }
    }
    return <code className={className}>{children}</code>;
  },
);

/** A source an inline citation marker can name. */
export interface CitationSource {
  /** 1-based number the marker refers to; matches the reference card. */
  number: number;
  name: string;
  url?: string | null;
}

/**
 * An inline citation badge — the source's name in a small pill, sitting on the
 * text baseline of the sentence that cited it.
 *
 * Named rather than numbered because a bare superscript digit tells the reader
 * nothing without looking away; the pill says which source it was. The number
 * still links the two and labels the matching card.
 *
 * Degrades to nothing on purpose. A marker whose number matches no source is
 * not a citation the reader can follow: the source list is what the cards are
 * built from, so a number outside it points at a card that does not exist.
 * Rendering the bare digit only left an unexplained number mid-sentence, so the
 * marker is dropped entirely and the prose closes over it.
 */
const makeCitationBadge = (
  citations: readonly CitationSource[],
): FC<{ children?: ReactNode; 'data-citation'?: string; dataCitation?: string }> =>
  observer(({ children, ...props }) => {
    // Read both spellings: which one arrives depends on how the hast property is
    // converted back to a JSX prop, and guessing wrong renders a bare number.
    const raw = props['data-citation'] ?? props.dataCitation;
    const source = raw ? citations.find((c) => c.number === Number(raw)) : undefined;

    // `children` is the bare number. Returning it would print a digit the reader
    // cannot resolve; returning nothing leaves the sentence reading normally.
    if (!source) return null;

    const activate = () => {
      if (source.url) {
        window.open(source.url, '_blank', 'noopener,noreferrer');
        return;
      }
      document
        .getElementById(`chat-reference-${source.number}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    return (
      // Pinned LTR: the badge's internal order is icon-then-label, which is a
      // chip's visual identity rather than reading order, so mirroring it inside
      // an Arabic answer would put the icon on the wrong side of its own label.
      // The badge as a whole still flows with the surrounding RTL text — only
      // its inner direction is fixed.
      // It renders as a <span>: a <div> inside a <p> is invalid nesting, and the
      // browser reshuffles the DOM rather than rendering it.
      <NoFlipBox
        mode="ltr"
        component="span"
        role="link"
        tabIndex={0}
        title={source.name}
        aria-label={`${source.number}. ${source.name}`}
        onClick={activate}
        onKeyDown={(event: ReactKeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activate();
          }
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25em',
          maxWidth: '12em',
          // Baseline-adjacent, not superscript: the badge belongs in the
          // sentence, and `vertical-align: super` lifted it out of the line.
          verticalAlign: 'baseline',
          mx: '0.2em',
          px: '0.45em',
          py: '0.05em',
          borderRadius: '1em',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'action.hover',
          color: 'text.secondary',
          fontSize: '0.8em',
          fontWeight: 500,
          lineHeight: 1.6,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.selected', color: 'text.primary' },
        }}
      >
        <FileTextIcon size="1em" style={{ flexShrink: 0 }} />
        <Box
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {source.name}
        </Box>
      </NoFlipBox>
    );
  });

const BASE_COMPONENTS: Components = { table: TableScroll, code: CodeFence };

/**
 * Inline mode drops the wrappers that would force a line break, so a markdown
 * fragment can sit inside a sentence — used for per-block and per-change text,
 * where the surrounding component already owns the paragraph.
 */
const INLINE_COMPONENTS: Components = {
  ...BASE_COMPONENTS,
  p: Passthrough,
  h1: Passthrough,
  h2: Passthrough,
  h3: Passthrough,
  h4: Passthrough,
  h5: Passthrough,
  h6: Passthrough,
};

export interface MarkdownProps {
  /** Raw markdown. Nullish or blank renders nothing. */
  children: string | null | undefined;
  /** Render without block wrappers, for text embedded in a line. */
  inline?: boolean;
  /** Override the wrapper element. Defaults to `div` in both modes. */
  component?: ElementType;
  /**
   * Sources that inline `[n]` citation markers refer to. Supply them and each
   * marker becomes a named badge; omit them and markers stay plain text.
   */
  citations?: readonly CitationSource[];
  sx?: SxProps<Theme>;
}

/**
 * Renders markdown produced by the AI.
 *
 * Every AI-authored string in the product — chat answers, generated document
 * blocks, proposed rewrites — comes back as markdown, so it must be rendered
 * rather than printed. GFM is on for tables and strikethrough.
 *
 * Raw HTML is rendered and then sanitized, rather than disabled. Model output
 * does contain markup, and escaping it only moves the problem: the reader gets
 * `<table>` as words instead of a table. `rehype-raw` parses it and
 * `rehype-sanitize` removes everything that could execute — see
 * `SANITIZE_SCHEMA` for what survives and why.
 *
 * ORDER MATTERS: raw must run before sanitize. Reversed, the markup is still a
 * text node when the sanitizer inspects it, so nothing is checked and the
 * dangerous version is what gets parsed.
 */
export const Markdown: FC<MarkdownProps> = observer(
  ({ children, inline = false, component, citations, sx }) => {
    // Rebuilt only when the source list changes — a new `components` object on
    // every render would remount every mapped node, and this one renders on each
    // streamed chunk.
    const components = useMemo<Components>(() => {
      const base = inline ? INLINE_COMPONENTS : BASE_COMPONENTS;
      // An empty list still installs the override. A caller that passes
      // `citations` at all is rendering an answer whose `[n]` markers were
      // already rewritten to <sup>, and the sources arrive part-way through the
      // stream — until they do, nothing resolves, and the default <sup> would
      // show those markers as bare superscript digits that later pop into
      // badges. Callers passing nothing keep the default: their superscripts are
      // ordinary markdown.
      if (!citations) return base;
      return { ...base, sup: makeCitationBadge(citations) };
    }, [inline, citations]);

    if (!children?.trim()) return null;

    return (
      <Box
        // Inline mode still uses a div (displayed inline) rather than a span:
        // a <table> or <pre> inside a span is invalid nesting, and the browser
        // rearranges the DOM rather than rendering it.
        component={component ?? 'div'}
        sx={[
          inline ? { display: 'inline' } : null,
          BLOCK_SX,
          // Inline text must not introduce block boxes — a list here would break
          // the line it is embedded in (and any line-clamp around it).
          inline
            ? {
                '& ul, & ol': { display: 'inline', margin: 0, padding: 0 },
                '& li': { display: 'inline', marginBottom: 0, '&::marker': { content: '""' } },
                '& > :last-child': { marginBottom: 0 },
              }
            : null,
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          // `rehypeRaw` parses the markup, `rehypeSanitize` then strips anything
          // executable from it. This order is load-bearing — see the note above.
          rehypePlugins={[rehypeRaw, [rehypeSanitize, SANITIZE_SCHEMA]]}
          components={components}
        >
          {children}
        </ReactMarkdown>
      </Box>
    );
  },
);
